import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { enableMapSet } from 'immer';

import { App } from '@nextroll/ar-app';
import { useContainerContext } from '@nextroll/ar-container-context';
import { provisioningClient } from '@nextroll/ar-feature-gating';
import i18n from '@nextroll/ar-i18n';
import telemetry from '@nextroll/telemetry';

import { getWorkflowsFeature } from '../../util/featureFlags';
import { IWorkflow } from '../../types/rofl';
import {
    getAppPath, getBasePath, getPrBasePath, getWorkflowRoute, getWorkflowNotFoundRoute,
} from '../../util/urls';
import useNotification from '../../hooks/useNotification';

import { WorkflowListPage } from '../pages/WorkflowListPage/WorkflowListPage';
import { PageNotFound } from '../pages/ErrorPage/ErrorPage';
import { WorkflowNotFound } from '../pages/WorkflowNotFoundPage/WorkflowNotFoundPage';
import { Notification } from './Notification/Notification';

enableMapSet();

/**
 * loading indicator
 */
const Loader = () => <div className='ar-page-loader' />;

export const WorkflowsApp = () => {
    const containerContext = useContainerContext();
    const {
        actualUser,
        advertisable,
        isReady,
        user,
        navigateTo,
    } = containerContext;

    const account = {
        actualUser,
        advertisable,
        isActualUserAdmin: !!actualUser?.granted_permission_names?.includes('VIEW_BACKSTAGE'),
        isActualUserEngineer: !!actualUser?.granted_permission_names?.includes('ENGINEER'),
        user,
    };

    const location = useLocation();

    const appPath = getAppPath();

    const [NotificationContext, alerts, addAlert, dismissAlert] = useNotification();

    const notificationContextValue = useMemo(() => (
        { alerts, addAlert, dismissAlert }), [alerts, addAlert, dismissAlert]
    );

    /**
     * Use this value to determine the content of the workflow canvas:
     * - IWorkflow: editing an existing workflow
     * - {}: create a new workflow
     * - undefined: canvas is closed
     */
    const [workflow, setWorkflow] = useState(undefined as IWorkflow | {} | undefined);

    /**
     * setup Telemetry configurations
     */
    useEffect(() => {
        if (!advertisable || !actualUser || !user) {
            return; // params for telemetry are not ready; do nothing
        }
        telemetry.setAdvertisable(advertisable as any);
        telemetry.setUser(actualUser as any, user);
    }, [advertisable, actualUser, user]);

    const handleClickNavItem = useCallback((url: string, event: React.MouseEvent) => {
        event.preventDefault();

        // RWPF-2548: We don't control the nav href values on the side bar from this app.
        // In PR builds this is a problem since the URLs are subtly rewritten, and some URLs
        // seem outside the app even when they are not.
        // In order to get around this, we replace the incoming URL with the PR build's.
        if (process.env.ROFL_PR_DEPLOY && url.startsWith(getBasePath()) && !url.startsWith(getPrBasePath())) {
            navigateTo({ to: url.replace(getBasePath(), getPrBasePath()) });
        } else {
            navigateTo({ to: url });
        }
    }, [navigateTo]);

    if (!isReady) {
        return (<Loader />);
    }

    // early return error page if
    // - feature subscription `WORKFLOWS` is NOT enabled
    if (!getWorkflowsFeature()?.enabled) {
        return (<PageNotFound />);
    }

    return (
        <App
            actualUser={account.actualUser}
            advertisable={account.advertisable}
            basePath={appPath}
            environment={process.env.ENVIRONMENT}
            i18n={i18n}
            onClickNavItem={handleClickNavItem}
            location={location}
            user={account.user}
            disableRollWorksTheme
            disableThemeLoaderComponent
            isNavItemActive={(href: string) => href.startsWith(appPath)}
            enableGlobalSearch={provisioningClient.getPackage()?.packageIsActive}
            showGlobalSearchRollWorksAdvertisingTab={false}
            showGlobalSearchRollWorksAdvertisingImpactTab
        >
            <NotificationContext.Provider value={notificationContextValue}>
                <Switch>
                    <Route exact path={getWorkflowRoute()}>
                        <WorkflowListPage workflow={workflow} setWorkflow={setWorkflow} />
                    </Route>
                    <Route path={getWorkflowNotFoundRoute()} component={WorkflowNotFound} />
                </Switch>
                <Notification />
            </NotificationContext.Provider>
        </App>
    );
};
